import ky from 'ky';
import { z } from 'zod';
import { ENV } from 'src/utils/env';
import { generateVRID } from 'src/utils/helpers';

const UPLOAD_CUSTOMER_CENTRE_FILE_SCHEMA = z.object({
  base64: z.string(),
  fileName: z.string(),
  type: z.string(),
  org_id: z.string(),
});

const UPLOAD_CUSTOMER_CENTRE_FILE_RESPONSE_SCHEMA = z.object({
  data: z.boolean(),
  error: z.string().optional().nullable(),
});

export type UploadCustomerCentreFileParam = z.infer<typeof UPLOAD_CUSTOMER_CENTRE_FILE_SCHEMA>;

export const uploadCustomerCentreFilePromise = async (param: UploadCustomerCentreFileParam) => {
  try {
    const parsedParam = UPLOAD_CUSTOMER_CENTRE_FILE_SCHEMA.safeParse(param);

    if (!parsedParam.success) {
      console.error(parsedParam.error);
      return false;
    }

    const uniqId = generateVRID();

    // create file storage id and sanitize file name
    const file_storage_id = `${param.org_id}_${param.fileName.replace(/[^\w\s]/g, '')}_${uniqId}`;

    const file_extension = param.type?.split?.('/').pop() || '';

    // get signed upload URL because base64 might be too large for netlify proxy
    const signedUploadUrlRes = await ky
      .post(`${ENV.FRONTEND_API_URL}/get_customer_centre_file_upload_url`, {
        json: {
          file_storage_id,
          file_extension,
        },
      })
      .json<{ data: string }>();

    if (!signedUploadUrlRes || !signedUploadUrlRes?.data) {
      console.error('Failed to get signed upload URL');
      return false;
    }

    const signedUploadUrl = signedUploadUrlRes.data;

    // upload
    const uploadRes = await ky.put(signedUploadUrl, {
      body: param.base64,
      headers: {
        'Content-Type': param.type,
      },
    });

    if (!uploadRes.ok) {
      const errorText = await uploadRes.text();
      console.error(`Failed to upload file: ${errorText}`);

      return false;
    }

    // update table
    const res = await ky
      .post(`${ENV.FRONTEND_API_URL}/attach_uploaded_customer_centre_file_to_org_id`, {
        json: {
          file_name: param.fileName,
          org_id: param.org_id,
          file_storage_id,
          file_extension,
        },
      })
      .json();

    const parsedRes = UPLOAD_CUSTOMER_CENTRE_FILE_RESPONSE_SCHEMA.safeParse(res);

    if (!parsedRes.success) {
      console.error(parsedRes.error.message);
      return false;
    }

    return parsedRes.data.data;
  } catch {
    console.error('An error occurred while uploading the file');
    return false;
  }
};
